import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { isEmpty, partition } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FeatureFlag } from '@revelio/core';
import type {
  SelectionList,
  SelectionListIdNames,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import { BreadcrumbTreeSelection } from '../../../../../../shared/components/tree/breadcrumb-tree/BreadcrumbTreeSelection';
import type { TreeApi } from '../../../../../../shared/components/tree/useTreeApi';
import { SelectionListControls } from '../../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useRoleTreeSelectionLists } from '../../../../../../shared/selection-lists/hooks/useRoleTreeSelectionLists';
import { useSelectionListsForKeys } from '../../../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import {
  type GqlSelectionListKey,
  type RoleSelectionListKey,
  roleSelectionListKeys,
} from '../../../../../../shared/selection-lists/types';
import { useUnleashFlag } from '../../../../../../shared/unleash-flags/use-unleash-flag';
import { useCustomTaxonomyFilterValue } from '../useReportsRoleCustomTaxonomy';
import type { ReportSelectionCategories } from '../utils';
import styles from './report-entity-selections-modal.module.css';
import { useReportsCustomSkills } from './useReportsCustomSkills';

const useReportBreadcrumbSelectionLists = (
  selectionLists: ReportSelectionCategories[]
) => {
  const [[roleLists, otherLists], setSelectionLists] = useState<
    [RoleSelectionListKey[], GqlSelectionListKey[]]
  >([[], []]);
  const { lists: customSkillLists } = useReportsCustomSkills(0);

  useEffect(
    () =>
      setSelectionLists((prevLists) => {
        const [roles, others] = partition(selectionLists, (s) =>
          roleSelectionListKeys.includes(s as RoleSelectionListKey)
        ) as [RoleSelectionListKey[], GqlSelectionListKey[]];

        // Exclude custom skills tokens from the generic selection list fetch
        const filteredOthers = others.filter((s) => {
          const key = String(s).toLowerCase();
          return key !== 'custom_skill' && key !== 'custom_skills';
        }) as GqlSelectionListKey[];

        const newLists: [RoleSelectionListKey[], GqlSelectionListKey[]] = [
          roles,
          filteredOthers,
        ];

        return JSON.stringify(prevLists) === JSON.stringify(newLists)
          ? prevLists
          : newLists;
      }),
    [selectionLists]
  );

  const { selectionLists: s } = useSelectionListsForKeys(otherLists);
  const customTaxonomyFilterValue = useCustomTaxonomyFilterValue();

  // All role groups are tree-backed via the role TaxonomyTree (any key set,
  // including the role_k7 / role_k15000 ends — the end-tag aliasing lives in
  // treeToRoleSelectionLists).
  const roleSelectionLists = useRoleTreeSelectionLists({
    requiredKeys: roleLists,
    taxonomySelection: customTaxonomyFilterValue,
  });

  return useMemo(() => {
    // Prefer a simple "k<number>" label when the key contains on
    const formatBreadcrumbLabel = (key: string) => {
      const kNumberMatch = key.match(/k\d+/i);
      return kNumberMatch
        ? kNumberMatch[0].toLowerCase()
        : key
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase())
            .replace(/K(\d+)/g, 'k$1');
    };

    const otherListObjects = Object.entries(s || {}).map(([key, value]) => {
      return {
        id: key as SelectionListIdNames,
        label: formatBreadcrumbLabel(key),
        value,
      };
    });

    const roleListObjects =
      roleLists.length && roleSelectionLists
        ? (roleLists.map((listKey) => ({
            id: listKey,
            label: formatBreadcrumbLabel(listKey),
            value: roleSelectionLists?.[listKey] ?? [],
          })) as SelectionList<ValidValueTypes>[])
        : [];

    const customSkillsRequested = selectionLists.some((s) =>
      String(s).toLowerCase().includes('custom_skill')
    );

    const customSkillObjects = customSkillsRequested
      ? (customSkillLists as SelectionList<ValidValueTypes>[])
      : [];

    return [
      ...roleListObjects,
      ...customSkillObjects,
      ...otherListObjects,
    ] as SelectionList<ValidValueTypes>[];
  }, [s, roleLists, roleSelectionLists, customSkillLists, selectionLists]);
};

export const ReportEntitySelectionsModal = ({
  isOpen,
  onClose,
  // searchInputRef,
  initialSelections = {},
  selectionLists = [],
  header,
  limit = 1,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  // searchInputRef: React.RefObject<HTMLInputElement>;
  header: string;
  selectionLists: ReportSelectionCategories[];
  initialSelections?: Record<string, TreeItem>;
  limit?: number;
  onSubmit?: (items: Record<string, TreeItem>) => void;
}) => {
  const [selections, setSelections] =
    useState<Record<string, TreeItem>>(initialSelections);
  const treeRef = useRef<TreeApi>(null);
  const breadcrumTreeSelectionLists =
    useReportBreadcrumbSelectionLists(selectionLists);
  const isSelectionListIdsEnabled = useUnleashFlag(
    FeatureFlag.SelectionListIds
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      // initialFocusRef={searchInputRef}
    >
      <ModalOverlay />
      <ModalContent className={styles.modalContent}>
        <ModalHeader className={styles.modalHeader}>{header}</ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody py="16px">
          <BreadcrumbTreeSelection
            ref={treeRef}
            initialSelections={initialSelections}
            branches={breadcrumTreeSelectionLists.map(
              (listName) => listName.label
            )}
            selectionLists={breadcrumTreeSelectionLists}
            maxSelections={limit}
            onChange={setSelections}
            isSelectionListIdsEnabled={isSelectionListIdsEnabled}
          />
        </ModalBody>
        <ModalFooter className={styles.modalFooter} width="100%">
          <SelectionListControls
            onClear={() => {
              setSelections({});
              treeRef.current?.clearAll();
            }}
            onClose={onClose}
            onSubmit={() => onSubmit?.(selections)}
            clearText="Reset"
            submitText="Submit"
            isSubmitDisabled={isEmpty(selections)}
            p="0"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
